// hooks
import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';

// types
import { PreferredEmployee } from 'components/shared/PreferredEmployees.types';
import {
  MatchCandidateResponse,
  MatchCandidateTypeEnum
} from 'RestAPIs/flex-match/types';
import { NoteResponse } from 'generated/index';
import {
  AnyRow,
  GridData,
  OpenReqPayload,
  TaskPayload,
  VertxMsg
} from './useFlexMatchSockets.types';

// utils
import EventBus, {
  FLEX_TASKS_ADDR,
  OPEN_REQUESTS_ADDR
} from 'lib/EventBusUpdated';
import {
  CANDIDATES_QUERY_KEY,
  FLEX_TASKS_QUERY_KEY,
  REQUEST_NOTES_QUERY_KEY,
  REQUEST_QUALIFICATIONS_QUERY_KEY
} from './useFlexMatchSockets.consts';
import { Qualification } from 'components/types';

export function useFlexMatchSockets(options: {
  enabled: boolean;
  pauseWhileMatching?: boolean;
}) {
  const { enabled, pauseWhileMatching } = options;
  const queryClient = useQueryClient();
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled && !pauseWhileMatching;
  }, [enabled, pauseWhileMatching]);

  useEffect(() => {
    if (!enabledRef.current) return;

    // ---------- helpers ----------
    const patchAllGrids = (updater: (grid: GridData) => GridData) => {
      queryClient.setQueriesData(
        [FLEX_TASKS_QUERY_KEY],
        (old: GridData | undefined) => {
          if (!old) return old as any;
          try {
            return updater(old);
          } catch {
            return old;
          }
        }
      );
    };

    const patchCandidates = (
      requestId: number,
      updater: (curr: any[]) => any[]
    ) => {
      const cache = queryClient.getQueryCache();
      const matches = cache.findAll({
        predicate: (query: any) => {
          const key = query.queryKey as any[];
          if (!Array.isArray(key) || key[0] !== CANDIDATES_QUERY_KEY)
            return false;
          const second = key[1];
          if (second === requestId) return true;
          if (
            second &&
            typeof second === 'object' &&
            (second as any).requestId === requestId
          )
            return true;
          return false;
        }
      });

      if (!matches || matches.length === 0) {
        queryClient.setQueryData<any[]>(
          [CANDIDATES_QUERY_KEY, requestId],
          (old) => updater(Array.isArray(old) ? old : [])
        );
        queryClient.setQueryData<any[]>(
          [CANDIDATES_QUERY_KEY, { requestId }],
          (old) => updater(Array.isArray(old) ? old : [])
        );
        return;
      }

      for (const q of matches) {
        queryClient.setQueryData<any[]>(q.queryKey, (old) => {
          const curr = Array.isArray(old) ? old : [];
          return updater(curr);
        });
      }
    };

    const patchRequestNotes = (
      requestId: number | string,
      updater: (curr: any[]) => any[]
    ) => {
      const idNum =
        typeof requestId === 'string' ? Number(requestId) : requestId;
      const cache = queryClient.getQueryCache();
      const matches = cache.findAll({
        predicate: (query: any) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key[0] !== REQUEST_NOTES_QUERY_KEY)
            return false;

          if (key.length >= 3 && (key[2] === idNum || key[2] === String(idNum)))
            return true;

          const second = key[1];
          if (second === idNum || second === String(idNum)) return true;
          if (
            second &&
            typeof second === 'object' &&
            second.requestId != null
          ) {
            const key = second.requestId;
            return key === idNum || key === String(idNum);
          }
          return false;
        }
      });

      if (!matches || matches.length === 0) {
        queryClient.setQueryData<any[]>(
          [REQUEST_NOTES_QUERY_KEY, 'list', idNum],
          (old) => updater(Array.isArray(old) ? old : [])
        );
        queryClient.setQueryData<any[]>(
          [REQUEST_NOTES_QUERY_KEY, { requestId: idNum }],
          (old) => updater(Array.isArray(old) ? old : [])
        );
        return;
      }

      for (const q of matches) {
        queryClient.setQueryData<any[]>(q.queryKey, (old) => {
          const curr = Array.isArray(old) ? old : [];
          return updater(curr);
        });
      }
    };

    const patchRequestQualifications = (
      requestId: number,
      updater: (curr: Qualification[]) => Qualification[]
    ): void => {
      const key1: [string, { requestId: number }] = [
        REQUEST_QUALIFICATIONS_QUERY_KEY,
        { requestId }
      ];
      const key2: [string, number] = [
        REQUEST_QUALIFICATIONS_QUERY_KEY,
        requestId
      ];

      const applyPatch = (key: [string, any]) => {
        queryClient.setQueryData<Qualification[]>(key, (old) => {
          const curr = Array.isArray(old) ? old : [];
          return updater(curr);
        });
      };

      applyPatch(key1);
      applyPatch(key2);
    };

    const normalizeNote = (note: any) => {
      if (!note) return null;
      if (typeof note === 'string') return { content: note };
      return {
        id: note.id ?? note.noteId ?? undefined,
        content: note.content ?? note.text ?? note.message ?? '',
        ...(note ?? {})
      };
    };

    const upsertRow = (rows: AnyRow[], incoming: AnyRow) => {
      const i = rows.findIndex((row) => row.requestId === incoming.requestId);
      if (i === -1) return [incoming, ...rows];
      const copy = rows.slice();
      copy[i] = { ...rows[i], ...incoming };
      return copy;
    };

    const unwrap = <T>(
      message: VertxMsg<T> | T | undefined | null
    ): T | null =>
      message && typeof message === 'object' && 'body' in (message as any)
        ? (message as VertxMsg<T>).body ?? null
        : (message as T) ?? null;

    const mergeKeepExisting = (prev: any, next: any) => {
      const out = { ...prev };
      for (const [key, value] of Object.entries(next ?? {})) {
        if (value === undefined || value === null) continue;
        if (key === 'matchCandidateTypes') {
          const a = Array.isArray(prev?.matchCandidateTypes)
            ? prev.matchCandidateTypes
            : [];
          const b = Array.isArray(next?.matchCandidateTypes)
            ? next.matchCandidateTypes
            : [];
          out.matchCandidateTypes = Array.from(new Set([...a, ...b]));
        } else {
          out[key] = value;
        }
      }

      return out;
    };

    // make toCandidate sparse (only include fields that exist)
    const toCandidate = (
      src: any,
      ctx: {
        taskId?: number;
        type?: MatchCandidateResponse['matchCandidateTypes'][number];
      } = {}
    ): Partial<MatchCandidateResponse> => {
      const out: Partial<MatchCandidateResponse> = {};

      const id = src?.employeeId ?? src?.empId;
      if (id != null) out.employeeId = id;

      const nr = src?.employeeNr ?? src?.nr;
      if (nr != null) out.employeeNr = nr;

      const name =
        src?.employeeName ??
        src?.name ??
        src?.nameSurname ??
        src?.nameSurnameCompound ??
        src?.nameCompound;
      if (name != null) out.employeeName = name;

      // copy only if present (no defaults)
      const copyKeys = [
        'workAgreementId',
        'workAgreementNr',
        'comEmail',
        'comPhone',
        'comPhoneAlt',
        'comSms',
        'employeeEntityId',
        'sourceCostCenter',
        'functionName',
        'balanceHours',
        'workRelation',
        'dutyOverlap',
        'availablePercentage',
        'wabPercentage',
        'matchedOn',
        'availabilities',
        'sameDayDutyIds',
        'matchRemark'
      ] as const;
      for (const k of copyKeys)
        if (src?.[k] !== undefined && src?.[k] !== null)
          (out as any)[k] = src[k];

      if (src?.askedTaskId != null) out.askedTaskId = src.askedTaskId;
      else if (ctx.taskId != null) out.askedTaskId = ctx.taskId;

      if (Array.isArray(src?.matchCandidateTypes))
        out.matchCandidateTypes = src.matchCandidateTypes;
      else if (ctx.type) out.matchCandidateTypes = [ctx.type];

      return out;
    };
    const isTaskCreateWithPreferred = (
      val
    ): val is {
      requestId: number;
      preferredEmployees: MatchCandidateResponse[];
      taskId?: string | number;
    } => {
      return (
        !!val &&
        typeof val.requestId === 'number' &&
        Array.isArray(val.preferredEmployees)
      );
    };

    // ---------- handlers ----------
    const onTasks = (_err: any, raw: any) => {
      const message = unwrap<TaskPayload>(raw);
      if (!enabledRef.current || !message?.type) return;

      if (message.type === 'create') {
        const upsertBody: AnyRow = {
          ...message.body,
          isNew: true,
          statusCode:
            message.body?.taskDefinitionKey === 'FlexPortalMatchAcceptRequest'
              ? 'send'
              : 'bpm'
        };

        // upsert/replace the row
        patchAllGrids((grid) => ({
          ...grid,
          data: upsertRow(grid.data, upsertBody)
        }));

        // sync candidates if preferredEmployees are present
        if (isTaskCreateWithPreferred(message.body)) {
          patchCandidates(message.body.requestId, (curr) => {
            const byId = new Map<number | string, any>(
              (curr ?? []).map((c) => [c.employeeId, c])
            );
            for (const pe of message.body.preferredEmployees) {
              const next = toCandidate(pe, {
                taskId: message.body?.taskId,
                type: MatchCandidateTypeEnum.PREFERRED_PLANNER
              });
              if (!next?.employeeId) continue;
              const prev = byId.get(next.employeeId);
              byId.set(
                next.employeeId,
                prev ? mergeKeepExisting(prev, next) : next
              );
            }
            return Array.from(byId.values());
          });
        }

        return;
      }

      if (message.type === 'update') {
        patchAllGrids((grid) => ({
          ...grid,
          data: upsertRow(grid.data, message.body)
        }));

        const body = message.body;
        if (Array.isArray(body?.preferredEmployees) && body.requestId) {
          patchCandidates(body.requestId, (curr) => {
            const byId = new Map<number | string, any>(
              (curr ?? []).map((c) => [c.employeeId, c])
            );
            for (const pe of body.preferredEmployees) {
              const next = toCandidate(pe, {
                taskId: body?.taskId,
                type: MatchCandidateTypeEnum.PREFERRED_PLANNER
              });
              if (!next?.employeeId) continue;
              const prev = byId.get(next.employeeId);
              byId.set(
                next.employeeId,
                prev ? mergeKeepExisting(prev, next) : next
              );
            }
            return Array.from(byId.values());
          });
        }
        return;
      }

      if (message.type === 'delete') {
        patchAllGrids((grid) => {
          const pid =
            message.body?.processInstanceId ??
            message.body?.pid ??
            message.body?.processId ??
            null;
          const rid = message.body?.requestId ?? null;

          let removedRequestId: number | undefined;
          const beforeLen = grid.data.length;

          const nextData = grid.data.filter((row) => {
            const match =
              (rid != null && row.requestId === rid) ||
              (pid != null && row.processInstanceId === pid);
            if (match) removedRequestId = row.requestId;
            return !match;
          });

          if (removedRequestId != null) {
            patchCandidates(removedRequestId, () => []);
          }

          const removedCount = beforeLen - nextData.length;
          return {
            ...grid,
            data: nextData,
            totalItems: Math.max(0, grid.totalItems - removedCount)
          };
        });

        return;
      }
    };

    const onOpenRequests = (_err: any, raw: OpenReqPayload) => {
      const message = unwrap<OpenReqPayload>(raw);
      if (!enabledRef.current || !message?.type) return;

      patchAllGrids((grid) => {
        const idx = grid.data.findIndex(
          (row) => row.requestId === message.body?.requestId
        );
        if (idx === -1) return grid;
        const row = grid.data[idx];

        const preferredFromMsg =
          message.body.preferredEmployee ?? message.body.employee ?? null;

        const nextRow = (() => {
          switch (message.type) {
            // Note: addNote is currently not working properly, WS returns the note id and not the full note object. Already an issue in dojo.
            case 'addNote': {
              const normalized = normalizeNote(message.body.note);
              return {
                ...row,
                notes: [
                  ...(row.notes ?? []).filter(Boolean),
                  normalized
                ].filter(Boolean)
              };
            }
            case 'removeNote': {
              const id = message.body.noteId ?? message.body.note?.id;
              return {
                ...row,
                notes: (row.notes ?? []).filter((note: NoteResponse) => {
                  if (!note) return false;
                  if (id != null) return note.id !== id;
                  const contentToRemove =
                    message.body.note?.content ?? message.body.note ?? '';
                  const content =
                    typeof note === 'string' ? note : note.content ?? '';
                  return content !== contentToRemove;
                })
              };
            }

            case 'removePreferredEmployee': {
              const id =
                message.body.employeeId ??
                message.body.empId ??
                preferredFromMsg?.empId ??
                preferredFromMsg?.employeeId;
              if (!id) return row;
              return {
                ...row,
                preferredEmployees: (row.preferredEmployees ?? []).filter(
                  (employee: PreferredEmployee) =>
                    (employee.empId ?? employee.employeeId) !== id
                )
              };
            }
            // Todo: implement updateStatusCode --> this is for updating function and duty code in match drawer top info content
            // a[
            //   '{"type":"rec","address":"dzz/roster/open-requests","body":{"type":"updateStatusCode","body":{"linkedToActivities":false,"flags":["ANY_CHANGED"],"date":"2023-10-22T22:00:00.000Z","functionId":43,"dutyCode":"OR2","functionName":"Verpleegkundige","costCenterName":"Krachtige zorg","costCenterNameShort":"Krachtige zorg","poolName":"something","clusterIds":[1374,1155],"functionColor":"#ecefb2","hoursForCalculation":6.0,"flexPortalMatchStatus":"CONNECT_EMPLOYEE","displayDutyCode":"OR2","displayDutyColorCode":"#005eff","isDutyValid":true,"isAutoMatch":false,"costCenterId":55,"statusCode":"bpm","requestId":50275,"periods":[{"startTime":"2023-10-23T05:00:00.000Z","endTime":"2023-10-23T11:00:00.000Z","overlapFlag":-1,"dutyHourType":{"flags":["TRAVEL_EXPENSES","EXS_DUTY_TIMES_EDIT","ALLOW_SWAP","EXTERNAL_DUTY_MARK","SHOW_IN_TEAMROSTER","AVAILABLE_DECENTRALIZED"],"description":"standaard dienst extern","code":"sde","isDefault":false,"id":162},"dutyPeriodId":2411}],"dutyId":2690,"poolId":1076,"hasSubscribers":false}}}'
            // ];
            // AND
            // a[
            //   '{"type":"rec","address":"dzz/secure/users/flexportal/tasks","body":{"type":"delete","body":{"processInstanceId":"602483"}}}'
            // ];

            case 'updateStatusCode':
              return { ...row, ...message.body };

            case 'removeQualification':
              return {
                ...row,
                qualifications: (row.qualifications ?? []).filter(
                  (qualification) =>
                    qualification.id !== message.body.qualificationId
                )
              };

            case 'addQualification':
              return {
                ...row,
                qualifications: [
                  ...(row.qualifications ?? []),
                  ...message.body.qualification
                ]
              };

            case 'update': {
              return { ...row, ...message.body };
            }
            default:
              return { ...row, ...message.body };
          }
        })();

        const nextData = grid.data.slice();
        nextData[idx] = nextRow;
        return { ...grid, data: nextData };
      });

      if (message.body?.requestId) {
        const reqId = message.body.requestId;

        // Note: addNote is currently not working properly, WS returns the note id and not the full note object. Already an issue in dojo.
        if (message.type === 'addNote') {
          const normalized = normalizeNote(message.body.note);
          if (normalized) {
            patchRequestNotes(reqId, (arr) => [...arr, normalized]);
          }
          return;
        }

        if (message.type === 'removeNote') {
          const id = message.body.noteId ?? message.body.note?.id;
          const contentToRemove =
            message.body.note?.content ?? message.body.note ?? '';

          patchRequestNotes(reqId, (arr) =>
            arr.filter((n: any) => {
              if (id != null) return n?.id !== id;
              const content = typeof n === 'string' ? n : n?.content ?? '';
              return content !== contentToRemove;
            })
          );
          return;
        }

        if (message.type === 'removePreferredEmployee') {
          const id =
            message.body.employeeId ??
            message.body.empId ??
            message.body.preferredEmployee?.employeeId ??
            message.body.employee?.employeeId ??
            message.body.preferredEmployee?.empId ??
            message.body.employee?.empId;

          if (id != null) {
            patchCandidates(reqId, (arr) =>
              (Array.isArray(arr) ? arr : []).filter(
                (c) => c?.employeeId !== id
              )
            );
          }
          return;
        }

        if (message.type === 'addQualification') {
          const q = message.body.qualification;
          patchRequestQualifications(message.body.requestId, (arr) => {
            // Prevent duplicate insert
            if (arr.find((item) => item.id === q.id)) return arr;
            return [...arr, q];
          });
          return;
        }

        if (message.type === 'removeQualification') {
          patchRequestQualifications(message.body.requestId, (arr) =>
            arr.filter(
              (item) => item.id !== String(message.body.qualificationId)
            )
          );
          return;
        }
      }
    };

    EventBus.register(FLEX_TASKS_ADDR, onTasks);
    EventBus.register(OPEN_REQUESTS_ADDR, onOpenRequests);

    return () => {
      EventBus.unregister(FLEX_TASKS_ADDR, onTasks);
      EventBus.unregister(OPEN_REQUESTS_ADDR, onOpenRequests);
    };
  }, [queryClient, enabled, pauseWhileMatching]);
}
